import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface HUDButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'cyan' | 'emerald' | 'red' | 'slate';
  active?: boolean;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  shortcut?: string;
}

export const HUDButton: React.FC<HUDButtonProps> = ({
  children,
  label,
  shortcut,
  variant = 'cyan',
  active,
  icon,
  className = '',
  ...props
}) => {
  const variants = {
    cyan: 'border-slate-700 text-slate-200 hover:border-cyan-700 hover:bg-cyan-950/50 hover:text-cyan-100 active:bg-cyan-900/50',
    emerald: 'border-slate-700 text-slate-200 hover:border-emerald-700 hover:bg-emerald-950/50 hover:text-emerald-100 active:bg-emerald-900/50',
    red: 'border-slate-700 text-slate-300 hover:border-red-700 hover:bg-red-950/50 hover:text-red-200 active:bg-red-900/50',
    slate: 'border-slate-700 text-slate-300 hover:border-slate-500 hover:bg-slate-800 active:bg-slate-700'
  };

  const activeStyles = {
    cyan: 'bg-cyan-950 border-cyan-400 text-cyan-100 ring-1 ring-inset ring-cyan-400',
    emerald: 'bg-emerald-950 border-emerald-400 text-emerald-100 ring-1 ring-inset ring-emerald-400',
    red: 'bg-red-950 border-red-400 text-red-100 ring-1 ring-inset ring-red-400',
    slate: 'bg-white/10 border-white/30 text-white'
  };

  const content = label ?? children;

  return (
    <button
      aria-pressed={active === undefined ? undefined : active}
      className={`
        relative flex min-h-9 items-center justify-center gap-2 rounded-md border px-3 py-1.5
        font-sans text-xs font-semibold transition-colors duration-150
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400
        focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 motion-reduce:transition-none
        ${active ? activeStyles[variant] : variants[variant]}
        ${props.disabled ? 'cursor-not-allowed opacity-40 grayscale' : 'cursor-pointer'}
        ${className}
      `}
      {...props}
    >
      {icon && <span className="shrink-0" aria-hidden="true">{icon}</span>}
      {content && <span className="min-w-0 truncate">{content}</span>}
      {shortcut && <kbd className="ml-auto shrink-0 font-mono text-[10px] font-normal text-slate-500">{shortcut}</kbd>}
    </button>
  );
};

export const HUDSlider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (val: number) => void;
  suffix?: string;
  color?: string;
}> = ({ label, value, min, max, step = 1, onChange, suffix = '', color = 'cyan' }) => (
  <div className="space-y-1.5">
    <div className="flex justify-between text-xs font-medium text-slate-400">
      <span>{label}</span>
      <span className={`text-${color}-400 font-mono`}>{value.toFixed(suffix === 'x' ? 1 : 0)}{suffix}</span>
    </div>
    <input
      type="range" min={min} max={max} step={step}
      value={value}
      onChange={(event) => onChange(parseFloat(event.target.value))}
      className={`h-1 w-full cursor-pointer appearance-none rounded-lg bg-slate-800 accent-${color}-500`}
    />
  </div>
);

export const HUDPanel: React.FC<{
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  color?: 'cyan' | 'emerald' | 'red';
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}> = ({ title, icon, children, color = 'cyan', className = '', collapsible = false, defaultCollapsed = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const colorClasses = {
    cyan: { title: 'text-cyan-300', dot: 'bg-cyan-400', border: 'border-cyan-900/60' },
    emerald: { title: 'text-emerald-300', dot: 'bg-emerald-400', border: 'border-emerald-900/60' },
    red: { title: 'text-red-300', dot: 'bg-red-400', border: 'border-red-900/60' }
  }[color];
  const headerContent = (
    <>
      <h3 className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${colorClasses.title}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${colorClasses.dot}`}></span>
        {icon} {title}
      </h3>
      {collapsible && (
        <span className="text-slate-500 transition-colors duration-150 group-hover:text-slate-200 motion-reduce:transition-none">
          {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </span>
      )}
    </>
  );

  return (
    <section className={`space-y-2 ${className}`}>
      {collapsible ? (
        <button
          type="button"
          aria-expanded={!isCollapsed}
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="group flex w-full cursor-pointer items-center justify-between rounded-sm py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          {headerContent}
        </button>
      ) : (
        <div className="flex w-full items-center justify-between py-1">{headerContent}</div>
      )}
      {!isCollapsed && (
        <div className={`rounded-lg border bg-slate-950/65 p-2.5 ${colorClasses.border}`}>
          {children}
        </div>
      )}
    </section>
  );
};
